import { popupWindowFeatures } from "../../lib/popupWindow";
import { getBasename } from "../../routes/basename";
import { DOCUMENT_VIEWER_PATH } from "../../routes/paths";
import { getOpenerLockKey } from "./coord/openerLock";

/** Prefix for the per-document `sessionStorage` payload handoff key. */
const SESSION_PAYLOAD_PREFIX = "dsc-doc-viewer-payload:";

/** Cross-tab staleness window for a document's opener lock (see `openerLock.ts`). */
const LOCK_STALE_THRESHOLD_MS = 5 * 60 * 1000;

/** Display metadata + identifiers needed to open a document's own viewer window. */
export interface OpenDocumentViewerWindowInput {
  appId: string;
  docId: string;
  fileName: string;
  reviewedBy?: string | null;
}

// Module-scoped so repeat clicks on the same document within this tab reuse
// the window reference; named windows (below) additionally dedupe across tabs
// in most browsers, backstopped by the opener lock.
const viewerWindows = new Map<string, Window>();

/**
 * The `sessionStorage` key a document window reads its display metadata from.
 * Exported so `DocumentViewer` can read the same key it's written under.
 * @param {string} documentId - The document id.
 * @returns {string} The `sessionStorage` key.
 */
export function documentPayloadStorageKey(documentId: string): string {
  return `${SESSION_PAYLOAD_PREFIX}${documentId}`;
}

/**
 * Opens (or refocuses) the dedicated viewer window for one specific document
 * (OR-22558: one window per document). Identifiers (`appId`/`docId`) travel in
 * the URL; display metadata (`fileName`/`reviewedBy`) travels via a
 * `sessionStorage` entry written just before `window.open()` — the browser
 * clones the opener's `sessionStorage` into the new same-origin window at
 * creation time, so the document window already has it on first render, with
 * no BroadcastChannel round trip (and no risk of a race with another document's
 * selection, since every document gets its own window and its own key).
 *
 * Note this handoff only happens at creation time: if the document's window is
 * already open, refocusing it (below) does NOT re-deliver updated metadata —
 * the browser only clones sessionStorage into a window once, when it's first
 * opened, and the already-open window never re-reads it afterwards. So a
 * `fileName`/`reviewedBy` change that happens while the window is already open
 * won't be reflected until that window is closed and reopened.
 * @param {OpenDocumentViewerWindowInput} document_ - The document to open.
 * @returns {void}
 */
export function openDocumentViewerWindow(document_: OpenDocumentViewerWindowInput): void {
  const { appId, docId, fileName, reviewedBy } = document_;
  const windowKey = `${appId}:${docId}`;
  const windowName = `dsc-doc-viewer-${windowKey}`;

  const existing = viewerWindows.get(windowKey);
  if (existing && !existing.closed) {
    existing.focus();
    return;
  }

  // If this document's viewer is already open in another tab, don't open a
  // second window for it. The lock is written by the viewer window itself on
  // mount (never here — see `getOpenerLockKey` doc comment).
  const lockKey = getOpenerLockKey(appId, docId);
  const lockData = localStorage.getItem(lockKey);
  if (lockData) {
    try {
      const { timestamp } = JSON.parse(lockData);
      const ageMs = Date.now() - timestamp;
      if (ageMs < LOCK_STALE_THRESHOLD_MS) {
        return;
      }
      // Stale lock (its window likely crashed/closed without running its
      // unload cleanup) — clear it so it doesn't linger in localStorage
      // indefinitely, then proceed to open a fresh window.
      localStorage.removeItem(lockKey);
    } catch {
      // Malformed lock data, clear it and proceed
      localStorage.removeItem(lockKey);
    }
  }

  // Write the handoff payload only when actually opening a new window — the
  // browser clones sessionStorage into it once, at creation time (see the doc
  // comment above). Writing this when refocusing an existing window above
  // would be a no-op, since that window never re-reads it.
  try {
    sessionStorage.setItem(documentPayloadStorageKey(docId), JSON.stringify({ fileName, reviewedBy }));
  } catch {
    // Storage unavailable/full (e.g. private browsing) — the window falls
    // back to showing the bare ids until content resolves.
  }

  const url = `${getBasename()}${DOCUMENT_VIEWER_PATH}?appId=${encodeURIComponent(appId)}&docId=${encodeURIComponent(docId)}`;
  const opened = globalThis.open(url, windowName, popupWindowFeatures()) ?? undefined;
  if (opened) {
    viewerWindows.set(windowKey, opened);
  }
  opened?.focus();
}
